import React from 'react';

/**
 * Branded SVG cliparts in the Physiogenic palette (#003D80 brand, #FFB000 accent).
 * Line-art / flat style — no AI slop, no purple gradients.
 */

const BRAND = '#003D80';
const AMBER = '#FFB000';
const AMBER_DEEP = '#E59800';

/** Physiotherapy treatment scene — therapist's hands working on a patient's
 *  knee/leg. Inline SVG, line-art style, brand-blue + amber palette.
 */
export function PhysioFigureClipart({ className = '' }) {
  return (
    <svg viewBox="0 0 420 420" xmlns="http://www.w3.org/2000/svg"
         className={className} aria-hidden="true">
      {/* Amber halo backdrop — strong */}
      <circle cx="210" cy="220" r="195" fill={AMBER} opacity="0.20"/>
      <circle cx="210" cy="220" r="160" fill={AMBER} opacity="0.10"/>

      {/* Treatment table */}
      <rect x="40" y="240" width="340" height="22" rx="4" fill="#FFFFFF" stroke={BRAND} strokeWidth="3"/>
      <line x1="60" y1="262" x2="60" y2="345" stroke={BRAND} strokeWidth="3" strokeLinecap="round"/>
      <line x1="360" y1="262" x2="360" y2="345" stroke={BRAND} strokeWidth="3" strokeLinecap="round"/>
      <line x1="60" y1="346" x2="380" y2="346" stroke={BRAND} strokeWidth="2" opacity="0.4" strokeLinecap="round"/>

      {/* Patient lying on table — torso + head + arms + leg with bent knee */}
      <g stroke={BRAND} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none">
        {/* Head */}
        <circle cx="80" cy="225" r="16" fill="#FAF6EE" stroke={BRAND} strokeWidth="3"/>
        {/* Hair tuft */}
        <path d="M72 215 Q 78 207 86 213" strokeWidth="2"/>
        {/* Torso */}
        <path d="M96 230 Q 145 232 195 235"/>
        {/* Belly curve */}
        <path d="M195 235 Q 220 240 230 245"/>
        {/* Upper leg flat */}
        <path d="M230 245 L 285 245"/>
        {/* Bent knee + lower leg lifted */}
        <path d="M285 245 Q 310 235 320 215 L 330 175"/>
        {/* Foot */}
        <path d="M330 175 Q 338 168 348 173"/>

        {/* Patient's near arm rest on torso */}
        <path d="M120 232 Q 140 248 165 240"/>
      </g>

      {/* Therapist's hands working on the patient's knee */}
      <g stroke={BRAND} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="#FFFFFF">
        {/* Hand 1 — under knee */}
        <path d="M260 195 Q 275 188 295 195 Q 305 200 308 215 L 298 232 Q 285 235 273 230 Q 263 220 260 210 Z"/>
        {/* Fingers detail on hand 1 */}
        <path d="M270 200 L 273 192 M 278 198 L 281 188 M 286 200 L 290 190" fill="none" strokeWidth="2"/>
        {/* Hand 2 — gripping the calf */}
        <path d="M310 175 Q 322 168 338 175 Q 348 182 350 198 L 340 215 Q 328 218 318 213 Q 310 200 308 188 Z"/>
        <path d="M318 178 L 322 170 M 328 176 L 332 167 M 337 180 L 341 172" fill="none" strokeWidth="2"/>
      </g>

      {/* Motion / pulse arcs around the knee — amber */}
      <g stroke={AMBER_DEEP} strokeWidth="3" strokeLinecap="round" fill="none">
        <path d="M275 145 Q 305 130 335 145" strokeDasharray="3 6" opacity="0.85"/>
        <path d="M265 125 Q 305 105 345 125" strokeDasharray="2 7" opacity="0.55"/>
      </g>

      {/* Amber sparks — emphasizes treatment area */}
      <g fill={AMBER}>
        <circle cx="262" cy="118" r="4"/>
        <circle cx="345" cy="100" r="5"/>
        <circle cx="305" cy="85" r="3"/>
      </g>

      {/* Pain indicator (red dot replaced by amber pulse) — at knee */}
      <circle cx="305" cy="208" r="9" fill={AMBER} opacity="0.55"/>
      <circle cx="305" cy="208" r="4" fill={AMBER_DEEP}/>

      {/* Healing/check small badge — amber, top-left */}
      <g transform="translate(70 95)">
        <circle cx="0" cy="0" r="24" fill={AMBER}/>
        <path d="M-10 0 L -3 8 L 11 -7" stroke="#FFFFFF" strokeWidth="4" strokeLinecap="round"
              strokeLinejoin="round" fill="none"/>
      </g>

      {/* Tiny brand text label */}
      <text x="210" y="395" textAnchor="middle"
            fontFamily="Manrope, system-ui, sans-serif" fontWeight="700" fontSize="11"
            letterSpacing="3" fill={BRAND} opacity="0.7">
        PHYSIOTHERAPY · RECOVERY · MOVEMENT
      </text>
    </svg>
  );
}

/** Shield with check + lock + bandage cross — for compliance section.
 *  Replaces the photo with a branded illustration.
 */
export function ComplianceShieldClipart({ className = '' }) {
  return (
    <svg viewBox="0 0 480 480" xmlns="http://www.w3.org/2000/svg"
         className={className} aria-hidden="true">
      {/* Amber halo backdrop — strong */}
      <circle cx="240" cy="240" r="220" fill={AMBER} opacity="0.20"/>
      <circle cx="240" cy="240" r="180" fill={AMBER} opacity="0.10"/>

      {/* Outer shield */}
      <path d="M240 60 L 380 110 L 380 240 C 380 330 320 400 240 425 C 160 400 100 330 100 240 L 100 110 Z"
            fill="#FFFFFF" stroke={BRAND} strokeWidth="6" strokeLinejoin="round"/>

      {/* Inner shield panel (light off-white) */}
      <path d="M240 95 L 350 135 L 350 240 C 350 315 305 370 240 392 C 175 370 130 315 130 240 L 130 135 Z"
            fill="#FAF6EE" stroke={BRAND} strokeWidth="2" opacity="0.65"/>

      {/* Big check inside */}
      <path d="M165 245 L 220 300 L 320 195"
            stroke={BRAND} strokeWidth="14" strokeLinecap="round" strokeLinejoin="round" fill="none"/>

      {/* Amber medical cross — top-right corner */}
      <g transform="translate(335 105)">
        <circle cx="0" cy="0" r="34" fill={AMBER}/>
        <rect x="-4" y="-18" width="8" height="36" fill="#FFFFFF" rx="2"/>
        <rect x="-18" y="-4" width="36" height="8" fill="#FFFFFF" rx="2"/>
      </g>

      {/* Lock — bottom-left corner */}
      <g transform="translate(115 365)">
        <circle cx="0" cy="0" r="32" fill={BRAND}/>
        <path d="M-10 -4 L -10 -10 A 10 10 0 0 1 10 -10 L 10 -4"
              stroke="#FFFFFF" strokeWidth="3" fill="none" strokeLinecap="round"/>
        <rect x="-12" y="-4" width="24" height="18" rx="3" fill="#FFFFFF"/>
        <circle cx="0" cy="4" r="3" fill={BRAND}/>
      </g>

      {/* Audit dots row at bottom */}
      <g fill={AMBER}>
        <circle cx="190" cy="345" r="6"/>
        <circle cx="240" cy="350" r="6"/>
        <circle cx="290" cy="345" r="6"/>
      </g>

      {/* GDPR / HIPAA / PL micro-labels */}
      <g fontFamily="Manrope, system-ui, sans-serif" fontWeight="700" fontSize="12" fill={BRAND}>
        <text x="240" y="158" textAnchor="middle" letterSpacing="2">PHYSIOGENIC</text>
        <text x="240" y="178" textAnchor="middle" letterSpacing="3" fontSize="9" opacity="0.65">HIPAA · GDPR</text>
      </g>
    </svg>
  );
}
